import {lazy, Suspense, useEffect, useId, useLayoutEffect, useRef, useState} from 'react';
import {Alert, Button, Form, Modal, Nav, Spinner, Tab} from 'react-bootstrap';
import {isAdminSession} from '../service/sessionRole';
import {PropertyCleanerFields, PropertyDurationsFields, PropertyInstructionFields, PropertyJobsContent} from './PropertyEditorFields';
import PropertyDetailsFields from './PropertyDetailsFields';
import {INSTRUCTION_TYPES} from './propertyEditorUtils';
import usePropertyEditor from './usePropertyEditor';
import useAutoAssignAvailability from './scheduler/autoAssign/useAutoAssignAvailability';
import './ManageProperty.css';

const CreateJobDateModal = lazy(() => import('./CreateJobDateModal'));
const PropertyGuestKeyModal = lazy(() => import('./PropertyGuestKeyModal'));

const TABS = [
    {key: 'overview', label: 'Overview'},
    {key: 'durations', label: 'Durations'},
    {key: 'cleaner', label: 'Cleaner'},
    {key: 'instructions', label: 'Instructions'},
    {key: 'jobs', label: 'Recent Jobs'},
];

export default function PropertyEditorPanel({
    property, activeTab: requestedTab, onTabChange, instructionType, onInstructionTypeChange,
    onSaved, onEditStateChange, navigationRequest, headerAction, footerAction, enableJobCreation = false, enableGuestKeyEditing = false,
}) {
    const panelId = useId();
    const [reloadConfirmation, setReloadConfirmation] = useState(null);
    const [guestKeyOpen, setGuestKeyOpen] = useState(false);
    const [isAdmin] = useState(isAdminSession);
    const cleanerRegions = useAutoAssignAvailability();
    const canEditCleaner = isAdmin && cleanerRegions.includes(property.region);
    const activeTab = requestedTab === 'cleaner' && !canEditCleaner ? 'overview' : requestedTab;
    const bodyRef = useRef(null);
    const continueRef = useRef(null);
    const navRef = useRef(null);
    const headingRef = useRef(null);
    const createButtonRef = useRef(null);
    const wasCalendarOpen = useRef(false);
    const guestKeyReturnFocus = useRef(null);
    const wasGuestKeyOpen = useRef(false);
    const wasConfirming = useRef(false);
    const activeKey = activeTab === 'instructions' ? instructionType : activeTab;
    const editor = usePropertyEditor({property, isAdmin, activeKey, onSaved, guestKeyOpen});
    const section = editor.activeSection;
    const hasDirty = editor.dirtyKeys.length > 0;
    const confirmation = navigationRequest || (reloadConfirmation ? {
        id: `reload-${reloadConfirmation}`,
        title: 'Discard unsaved changes?',
        message: 'Unsaved edits in this section will be replaced with the saved values.',
        confirmLabel: 'Discard and reload',
        onConfirm: () => { editor.load(reloadConfirmation); setReloadConfirmation(null); },
        onCancel: () => setReloadConfirmation(null),
    } : null);
    const confirmationId = confirmation?.id;
    const navigationBusy = editor.busy || editor.jobCreation.open || Boolean(confirmation);

    useLayoutEffect(() => {
        onEditStateChange?.({propertyId: property.property_id, dirty: hasDirty, busy: navigationBusy});
    }, [property.property_id, hasDirty, navigationBusy, onEditStateChange]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [activeKey, confirmationId]);

    useEffect(() => {
        if (confirmationId) continueRef.current?.focus();
        else if (wasConfirming.current) navRef.current?.querySelector('[aria-selected="true"]')?.focus();
        wasConfirming.current = Boolean(confirmationId);
    }, [confirmationId]);

    useEffect(() => {
        if (editor.jobCreation.open) wasCalendarOpen.current = true;
        else if (wasCalendarOpen.current && !editor.busy) {
            // The calendar may first load through Suspense. Restore focus once for the whole flow.
            const target = createButtonRef.current?.disabled ? headingRef.current : createButtonRef.current;
            target?.focus({preventScroll: true});
            wasCalendarOpen.current = false;
        }
    }, [editor.jobCreation.open, editor.busy]);

    useEffect(() => {
        if (guestKeyOpen) wasGuestKeyOpen.current = true;
        else if (wasGuestKeyOpen.current && !editor.busy) {
            (guestKeyReturnFocus.current || headingRef.current)?.focus({preventScroll: true});
            wasGuestKeyOpen.current = false;
        }
    }, [guestKeyOpen, editor.busy]);

    useEffect(() => {
        if (!hasDirty && !editor.busy) return undefined;
        const beforeUnload = event => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, [hasDirty, editor.busy]);

    function reload() {
        if (section.needsReload) editor.load(activeKey, true);
        else if (editor.dirtyKeys.includes(activeKey)) setReloadConfirmation(activeKey);
        else editor.load(activeKey);
    }

    function isTabDirty(key) {
        return key === 'instructions'
            ? INSTRUCTION_TYPES.some(type => editor.dirtyKeys.includes(type.value))
            : editor.dirtyKeys.includes(key);
    }

    function openJobCalendar() {
        const field = editor.openJobCalendar();
        if (!field) return;
        const group = bodyRef.current?.querySelector(`[data-job-field="${field}"]`);
        const control = group?.querySelector('input, select, textarea') || group;
        control?.focus({preventScroll: true});
        control?.scrollIntoView({block: 'center'});
    }

    const editableTab = ['durations', 'instructions'].includes(activeTab) || (isAdmin && activeTab === 'overview')
        || (canEditCleaner && activeTab === 'cleaner');
    const saveLabel = {overview: 'Update Property', durations: 'Save Durations', cleaner: 'Save Cleaner', instructions: 'Save Instructions'}[activeTab];

    return <section className="property-editor-panel" aria-labelledby={`${panelId}-title`}>
        <header className="property-editor-header">
            <div className="property-editor-inner">
                {headerAction}
                <h2 ref={headingRef} id={`${panelId}-title`} tabIndex={-1}>{editor.sections.overview.baseline?.full_address || property.full_address || `Property #${property.property_id}`}</h2>
            </div>
        </header>
        <Tab.Container activeKey={activeTab} onSelect={key => { if (!navigationBusy) onTabChange(key); }}
            generateChildId={(key, type) => `${panelId}-${type}-${key}`} transition={false}>
            <Nav ref={navRef} variant="tabs" className="property-editor-tabs" aria-label="Property sections">
                {TABS.filter(tab => tab.key !== 'cleaner' || canEditCleaner).map(tab => (
                    <Nav.Item key={tab.key}>
                        <Nav.Link as="button" type="button" eventKey={tab.key} disabled={navigationBusy}>
                            {tab.label}
                            {isTabDirty(tab.key) ? <span className="property-editor-dirty"><span className="visually-hidden"> (unsaved changes)</span></span> : null}
                        </Nav.Link>
                    </Nav.Item>
                ))}
            </Nav>
            <div ref={bodyRef} className="property-editor-body"><div className="property-editor-inner">
                {editor.jobCreation.created ? <Alert variant={editor.jobCreation.refreshError ? 'warning' : 'success'} role="status">
                    <div>Job{editor.jobCreation.jobId ? ` #${editor.jobCreation.jobId}` : ''} created successfully.
                        {editor.jobCreation.jobId ? <> <a href={`/manage-job/update-job/${encodeURIComponent(editor.jobCreation.jobId)}`} target="_blank" rel="noreferrer">View Job</a></> : null}
                    </div>
                    {editor.jobCreation.refreshError ? <><div className="mt-1">{editor.jobCreation.refreshError}</div>
                        <Button className="mt-2" variant="outline-primary" size="sm" disabled={editor.busy} onClick={editor.retryJobRefresh}>Retry refresh</Button>
                    </> : editor.savingKey === 'job-refresh' ? <div className="mt-1">Refreshing property details…</div> : null}
                </Alert> : null}
                {editor.jobCreation.uncertain && !editor.jobCreation.open ? <Alert variant="warning">
                    <div>The creation result could not be confirmed. Check jobs on {editor.jobCreation.attemptedDate} before retrying; a Job may already exist.</div>
                    {editor.jobCreation.verificationError ? <div className="text-danger mt-2" role="alert">{editor.jobCreation.verificationError}</div> : null}
                    {editor.jobCreation.verifiedJobs != null ? <div className="mt-2">
                        {editor.jobCreation.verifiedJobs.length ? <PropertyJobsContent jobs={editor.jobCreation.verifiedJobs} /> : 'No jobs found on this date.'}
                    </div> : null}
                    <div className="property-editor-confirm-actions mt-2">
                        <Button size="sm" variant="outline-primary" disabled={editor.busy} onClick={editor.verifyJobAttempt}>
                            {editor.savingKey === 'job-check' ? 'Checking…' : 'Check jobs for this date'}</Button>
                        <Button size="sm" variant="outline-secondary" disabled={editor.busy || editor.jobCreation.verifiedJobs == null}
                            onClick={() => { editor.allowJobRetry(); onTabChange('overview'); }}>I checked; allow another attempt</Button>
                    </div>
                </Alert> : null}
                {confirmation ? <section aria-labelledby={`${panelId}-confirm-title`}>
                    <h3 id={`${panelId}-confirm-title`} className="h5">{confirmation.title}</h3>
                    <p>{confirmation.message}</p>
                    <div className="property-editor-confirm-actions">
                        <Button ref={continueRef} variant="outline-secondary" onClick={confirmation.onCancel}>Continue editing</Button>
                        <Button variant="danger" onClick={confirmation.onConfirm}>{confirmation.confirmLabel}</Button>
                    </div>
                </section> : <Tab.Content>
                    <Tab.Pane eventKey={activeTab}>
                        {activeTab === 'instructions' ? <Form.Group className="mb-4" controlId="property-instruction-type">
                            <Form.Label>Instruction type</Form.Label>
                            <Form.Select value={instructionType} disabled={editor.busy} onChange={event => onInstructionTypeChange(event.target.value)}>
                                {INSTRUCTION_TYPES.map(type => <option key={type.value} value={type.value}>
                                    {type.label}{editor.dirtyKeys.includes(type.value) ? ' •' : ''}
                                </option>)}
                            </Form.Select>
                        </Form.Group> : null}
                        {section?.error ? <Alert variant="danger">
                            <div>{section.error}</div>
                            {section.needsReload ? <div className="mt-1">Refresh the saved state to continue. Your draft will be kept.</div> : null}
                            {section.checklistConflict ? <Button className="mt-2" size="sm" variant="outline-danger"
                                disabled={editor.busy} onClick={editor.reloadPropertyTasks}>Replace task edits with latest</Button> : <Button className="mt-2" size="sm" variant="outline-danger" disabled={editor.busy || section.status === 'loading'} onClick={reload}>
                                {section.needsReload ? 'Refresh saved state' : section.value == null ? 'Retry' : 'Reload saved values'}
                            </Button>}
                        </Alert> : null}
                        {section?.notice ? <Alert variant="success" role="status">{section.notice}</Alert> : null}
                        {editor.validationError && !Object.keys(editor.jobValidationErrors).length ? <Alert variant="warning">{editor.validationError}</Alert> : null}
                        {!section || ['idle', 'loading'].includes(section.status) ? <div className="property-editor-loading" role="status">
                            <Spinner animation="border" size="sm" /><span>Loading…</span>
                        </div> : section.value != null ? <fieldset disabled={editor.busy || editor.jobCreation.open || section.needsReload || section.status !== 'ready'
                            || (activeTab === 'overview' && (!isAdmin || editor.jobCreation.needsRefresh))} className="property-editor-fields">
                            {activeTab === 'overview' ? <PropertyDetailsFields value={section.value} jobTypes={section.jobTypes} cleaningTypes={section.cleaningTypes}
                                consumableTypes={section.consumableTypes}
                                onEditGuestKey={enableGuestKeyEditing && isAdmin ? event => {
                                    if (navigationBusy) return;
                                    guestKeyReturnFocus.current = event.currentTarget;
                                    setGuestKeyOpen(true);
                                } : undefined}
                                jobDraft={enableJobCreation && isAdmin ? editor.jobDraft : null} onJobChange={editor.changeJobDraft} errors={editor.jobValidationErrors}
                                onChange={patch => editor.change('overview', current => ({...current, ...patch}))} /> : null}
                            {activeTab === 'durations' ? <PropertyDurationsFields durations={section.value}
                                onChange={(id, minutes) => editor.change('durations', current => current.map(duration => duration.duration_type_id === id
                                    ? {...duration, duration_minutes: minutes} : duration))} /> : null}
                            {activeTab === 'cleaner' && canEditCleaner ? <PropertyCleanerFields rule={section.value} skills={section.skills}
                                directory={section.directory} region={property.region}
                                onChange={patch => editor.change('cleaner', current => ({...current, ...patch}))} /> : null}
                            {activeTab === 'instructions' ? <PropertyInstructionFields key={instructionType} instruction={section.value}
                                type={INSTRUCTION_TYPES.find(type => type.value === instructionType).label}
                                onChange={patch => editor.change(instructionType, current => ({...current, ...patch}))}
                                onImagesDeleted={images => editor.imagesDeleted(instructionType, images)}
                                onUploadingChange={editor.setUploading} onDeletingChange={editor.setDeleting}
                                onUploadError={error => editor.setUploadError(instructionType, error)} /> : null}
                            {activeTab === 'jobs' ? <PropertyJobsContent jobs={section.value} /> : null}
                        </fieldset> : null}
                    </Tab.Pane>
                </Tab.Content>}
            </div></div>
        </Tab.Container>
        {!confirmation && (editableTab || footerAction) ? <footer className="property-editor-footer">
            <div className="property-editor-inner property-editor-footer-actions">
                {footerAction}
                {editableTab ? <Button onClick={editor.save} disabled={!editor.canSave} variant={activeTab === 'overview' && enableJobCreation ? 'outline-primary' : 'primary'}>
                    {editor.savingKey === activeKey ? <><Spinner animation="border" size="sm" className="me-2" />Saving…</> : saveLabel}
                </Button> : null}
                {enableJobCreation && isAdmin && activeTab === 'overview' ? <Button ref={createButtonRef} onClick={openJobCalendar} disabled={!editor.canCreateJob}>Create Job</Button> : null}
            </div>
        </footer> : null}
        {guestKeyOpen && isAdmin ? <Suspense fallback={<Modal show centered restoreFocus={false} onHide={() => setGuestKeyOpen(false)} aria-label="Guest Key settings">
            <Modal.Body role="status"><Spinner animation="border" size="sm" className="me-2" />Loading Guest Key settings…</Modal.Body>
        </Modal>}>
            <PropertyGuestKeyModal propertyId={property.property_id} onSettings={editor.applyGuestKeySettings}
                onClose={() => setGuestKeyOpen(false)} />
        </Suspense> : null}
        {enableJobCreation && editor.jobCreation.open ? <Suspense fallback={<Modal show centered restoreFocus={false} onHide={editor.closeJobCalendar} aria-label="Create Job">
            <Modal.Body role="status"><Spinner animation="border" size="sm" className="me-2" />Loading calendar…</Modal.Body>
        </Modal>}>
            <CreateJobDateModal address={editor.sections.overview.value.full_address} busy={editor.savingKey === 'job'}
                error={editor.jobCreation.error} uncertain={editor.jobCreation.uncertain}
                onConfirm={editor.confirmJobCreation} onCancel={editor.closeJobCalendar} onDateChange={editor.clearJobDateError} />
        </Suspense> : null}
    </section>;
}
